/** First token of a "remote scope" slug (e.g. "remote_europe") — no real city carries these names. */
const REMOTE_SCOPE_MARKER_WORDS = new Set(['remote', 'etätyö', 'etatyo', 'distans']);

/**
 * Reverses {@link toLocationSlug} for URL path segments: turns slugs like
 * `Helsinki_Finland` or `Remote_Europe` back into facet/display strings.
 */
export function decodeLocationSlug(raw: string | null | undefined): string {
  if (!raw) return '';
  let decoded = raw;
  try {
    decoded = decodeURIComponent(raw);
  } catch {
    // ignore decoding issues
  }
  // Defensive fallback for a stale bookmark/cache still using the old "_-_" slug shape.
  if (decoded.includes('_-_')) {
    return decoded.replace(/_/g, ' ').trim();
  }
  if (decoded.includes('_')) {
    const parts = decoded.split('_');
    // Remote-scope hubs ("remote_europe") reconstruct with " - ", not ", " — matching the
    // literal facet value used to tag remote-scoped jobs (e.g. "Remote - Europe"). Safe to
    // special-case unconditionally: no real city is named Remote/Etätyö/Distans.
    if (parts.length === 2 && REMOTE_SCOPE_MARKER_WORDS.has(parts[0].toLowerCase())) {
      return `${parts[0]} - ${parts[1]}`.trim();
    }
    if (parts.length === 2) {
      const [, second] = parts;
      const countrySuffixes = ['States', 'Kingdom', 'Arabia', 'Emirates', 'Republic', 'Federation'];
      if (countrySuffixes.some((suffix) => second.includes(suffix))) {
        return decoded.replace(/_/g, ' ').trim();
      }
    } else if (parts.length >= 3) {
      const countrySuffixes = ['States', 'Kingdom', 'Arabia', 'Emirates', 'Republic', 'Federation'];
      const lastPart = parts[parts.length - 1];
      if (countrySuffixes.some((suffix) => lastPart.includes(suffix))) {
        if (lastPart === 'States' && parts[parts.length - 2] === 'United') {
          const cityParts = parts.slice(0, -2);
          return `${cityParts.join(' ')}, United States`.trim();
        }
        const cityParts = parts.slice(0, -1);
        return `${cityParts.join(' ')}, ${lastPart}`.trim();
      }
    }
    return decoded.replace(/_/g, ', ').trim();
  }
  return decoded.trim();
}
